import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { nonNullable } from 'lib/array/arrayUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'

type ServerBookingDetails = definitions['BookingDetails']
type ServerBookingProperty = NonNullable<ServerBookingDetails['property']>
type ServerRoomContent = NonNullable<ServerBookingDetails['room']>
type ServerBookingRate = ServerBookingDetails['rate']
type ServerBookingCancellationPolicy = ServerBookingDetails['cancellationPolicy']
type ServerBookingInclusion = definitions['BookingInclusion']
type ServerImage = definitions['Image']

function optional<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined
}

function mapImage(image: ServerImage): App.Image {
  return {
    id: image.id,
    title: optional(image.title),
  }
}

/**
 * svc-accommodation returns the display text on `text`; `name` is the short label where present.
 *
 * The icon key is resolved through the shared allowlist rather than passed through, so an icon
 * svc-accommodation adds later renders the default rather than an unknown symbol.
 */
function mapInclusion(
  inclusion: ServerBookingInclusion,
  index: number,
): App.AccommodationBookingInclusion {
  return {
    id: inclusion.code ?? `${inclusion.text}-${index}`,
    description: inclusion.text,
    name: optional(inclusion.name),
    symbol: determineOfferFeatureSymbol(optional(inclusion.icon)),
  }
}

function mapInclusions(
  inclusions: Array<ServerBookingInclusion>,
): Array<App.AccommodationBookingInclusion> {
  return inclusions.map(mapInclusion)
}

/** "City, Region" where both exist, else whichever single part svc-accommodation returned. */
function mapLocationLabel(location: ServerBookingProperty['location']): string | undefined {
  const parts = nonNullable([location.city, location.region ?? location.countryName])
  return parts.length > 0 ? parts.join(', ') : undefined
}

function mapProperty(property: ServerBookingProperty): App.AccommodationBookingProperty {
  return {
    id: property.id,
    name: property.name,
    images: property.images.map(mapImage),
    description: optional(property.description),
    finePrint: optional(property.finePrint),
    locationLabel: mapLocationLabel(property.location),
    address: optional(property.location.address),
    latitude: optional(property.location.latitude),
    longitude: optional(property.location.longitude),
    timezone: optional(property.location.timezone),
    checkInFrom: optional(property.checkIn.beginTime),
    checkInUntil: optional(property.checkIn.endTime),
    checkInInstructions: optional(property.checkIn.instructions),
    checkOutBy: optional(property.checkOut.time),
    mandatoryFees: optional(property.fees.mandatory),
    optionalFees: optional(property.fees.optional),
    policies: property.policies,
    general: property.general,
    pets: property.pets,
    safety: property.safety,
  }
}

function mapRoom(room: ServerRoomContent): App.AccommodationBookingRoom {
  return {
    id: room.id,
    name: room.name,
    description: optional(room.copy?.description?.value),
    images: room.images.map(mapImage),
    roomSize: optional(room.roomSize),
    numberOfBedrooms: room.numberOfBedrooms,
    numberOfBathrooms: optional(room.numberOfBathrooms),
    bedGroupDescriptions: room.bedGroupDescriptions,
    amenityGroups: room.amenityGroups.map(group => ({
      name: group.name,
      values: group.values.map(amenity => amenity.name),
    })),
    policy: optional(room.copy?.roomPolicy?.value),
  }
}

function mapRate(rate: ServerBookingRate): App.AccommodationBookingRate {
  return {
    name: optional(rate.name),
    boardCode: optional(rate.boardCode),
    rateId: optional(rate.rateId),
    ratePlanId: optional(rate.ratePlanId),
    rateGroupId: optional(rate.rateGroupId),
    inclusions: mapInclusions(rate.inclusions),
    bonusInclusions: mapInclusions(rate.bonusInclusions),
    luxPlusInclusions: mapInclusions(rate.luxPlusInclusions),
    bundleInclusions: mapInclusions(rate.bundleInclusions),
  }
}

function mapCancellationPolicy(
  cancellationPolicy: ServerBookingCancellationPolicy,
): App.AccommodationBookingCancellationPolicy {
  return {
    refundableStatus: optional(cancellationPolicy.refundableStatus),
    fullRefundableUntil: optional(cancellationPolicy.fullRefundableUntil),
    partiallyRefundableUntil: optional(cancellationPolicy.partiallyRefundableUntil),
    policies: cancellationPolicy.policies.map(policy => ({
      start: policy.start,
      end: policy.end,
      amount: policy.amount,
      percent: policy.percent,
      description: optional(policy.description),
      nonRefundable: policy.nonRefundable,
    })),
  }
}

export function mapAccommodationBookingDetails(
  booking: ServerBookingDetails,
): App.AccommodationBookingDetails {
  return {
    id: booking.id,
    orderId: optional(booking.orderId),
    orderItemId: optional(booking.orderItemId),
    externalStatus: booking.externalStatus,
    stay: {
      checkIn: booking.stay.checkIn,
      checkOut: booking.stay.checkOut,
      numberOfNights: booking.stay.numberOfNights,
      guestFirstName: optional(booking.stay.guestFirstName),
      guestLastName: optional(booking.stay.guestLastName),
      specialRequests: optional(booking.stay.specialRequests),
      occupancy: booking.stay.occupancy,
    },
    property: booking.property ? mapProperty(booking.property) : undefined,
    room: booking.room ? mapRoom(booking.room) : undefined,
    rate: mapRate(booking.rate),
    cancellationPolicy: mapCancellationPolicy(booking.cancellationPolicy),
    warnings: booking.warnings,
  }
}
